/**
 * VecinosCapa.tsx — Términos municipales vecinos y nombres en el mapa de P3 (VECMUN-b).
 *
 * Se monta dentro del `MapContainer` de `MapaApoyoCotejo` y, al abrir P3 (sin precarga en
 * P1/P2), pide el índice de vecinos y el contorno de cada vecino del municipio propio.
 *
 * Especificación sellada por Luis (2-nov-2026):
 *  - Línea A para los vecinos: blanca continua, opacidad 0,9, grosor `max(1, grosor − 0,5)`
 *    (siempre más fina que la amarilla del propio), halo negro de grosor + 2 y opacidad 0,45,
 *    sin relleno y no interactiva.
 *  - Nombre de cada vecino junto a la linde común (punto `v` del índice): blanco, 12 px, peso 600.
 *  - Nombre del propio: amarillo `#facc15`, 14 px, peso 700, en el punto `e` del índice.
 *  - Nombres solo desde zoom 10; por debajo, solo líneas.
 *  - Capas: dos panes propios de Leaflet, 390 (líneas) y 395 (nombres), por debajo del
 *    `overlayPane` (400), donde van el contorno amarillo CONTP3 y los `CircleMarker` del cotejo:
 *    nada de esto tapa el contorno propio ni los puntos.
 *
 * Fallos (DOP-2): si falla el índice o el contorno de un vecino, ese vecino no se pinta (ni
 * su nombre) y se avisa con `console.warn`; el contorno propio, los puntos y el resto del
 * mapa no se ven afectados. `loadBoundaryByINE` recuerda el fallo (sin reintentos en bucle).
 *
 * @version 1.0.0
 */

import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { GeoJSON, Marker, Pane } from 'react-leaflet';
import L from 'leaflet';
import type { LatLngTuple } from 'leaflet';
import { typography } from '@/design-system/v2';
import { loadBoundaryByINE } from '@/services/boundaries/MunicipalBoundaryService';
import {
  loadNeighborsIndex,
  getNeighborsOf,
  type VecinoPunto,
} from '@/services/boundaries/NeighborsIndex';
import type { MunicipalBoundary } from '@/lib/spatialValidation';

const PANE_LINEAS = 'vecmun-lineas';
const PANE_NOMBRES = 'vecmun-nombres';
const Z_LINEAS = 390;
const Z_NOMBRES = 395;
// Los nombres solo desde este zoom (sellado).
const ZOOM_NOMBRES_MIN = 10;
// Sombra de texto de la vista previa aprobada (`previa.html`).
const SOMBRA_TEXTO = '0 0 2px #000, 0 0 2px #000, 0 0 3px #000, 0 0 4px #000';

export interface VecinosCapaProps {
  /** Código INE 5 dígitos del municipio propio. */
  ine5: string;
  /** Zoom actual del mapa (los nombres aparecen desde 10). */
  zoom: number;
  /** Grosor de la línea del contorno propio a este zoom; la de los vecinos es 0,5 px más fina. */
  grosor: number;
}

interface Propio {
  nombre: string;
  pos: LatLngTuple;
}

/** Lo cargado para un municipio. Lleva su INE: lo de otro municipio se ignora, sin reiniciar estado en el efecto. */
interface Carga {
  ine5: string;
  propio: Propio;
  vecinos: VecinoPunto[];
  contornos: Record<string, MunicipalBoundary>;
}

/** Etiqueta de texto sin interacción. El texto va por `textContent`, nunca como HTML. */
function crearIcono(texto: string, propio: boolean): L.DivIcon {
  const el = document.createElement('div');
  el.textContent = texto;
  Object.assign(el.style, {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    fontFamily: typography.fontFamily.sans,
    color: propio ? '#facc15' : '#ffffff',
    fontSize: propio ? '14px' : '12px',
    fontWeight: propio ? '700' : '600',
    letterSpacing: propio ? '0.02em' : 'normal',
    textShadow: SOMBRA_TEXTO,
  });
  return L.divIcon({ className: 'vecmun-nombre', html: el, iconSize: [0, 0] });
}

export const VecinosCapa: FC<VecinosCapaProps> = ({ ine5, zoom, grosor }) => {
  const [carga, setCarga] = useState<Carga | null>(null);
  // Capas de línea (no los halos), para subirlas por encima de todos los halos al llegar otro vecino.
  const lineas = useRef(new Map<string, L.GeoJSON>());

  const ineUtil = /^\d{5}$/.test(ine5) && ine5 !== '00000';

  // Carga al abrir P3. Guard `active` contra setState tras desmontaje.
  useEffect(() => {
    if (!ineUtil) return;
    let active = true;
    const cargar = async () => {
      const hayIndice = await loadNeighborsIndex();
      if (!active || !hayIndice) return; // el aviso ya lo emitió el índice
      const de = getNeighborsOf(ine5);
      if (!de) {
        console.warn(`[VECMUN] ${ine5} no figura en el índice de términos vecinos: sin vecinos en el mapa.`);
        return;
      }
      setCarga({ ine5, propio: { nombre: de.nombre, pos: [de.lat, de.lng] }, vecinos: de.vecinos, contornos: {} });
      const fallidos: string[] = [];
      // En paralelo; cada contorno se pinta al llegar.
      await Promise.all(de.vecinos.map(async (v) => {
        const b = await loadBoundaryByINE(v.ine).catch(() => null);
        if (!active) return;
        if (b) {
          setCarga((prev) => (prev && prev.ine5 === ine5
            ? { ...prev, contornos: { ...prev.contornos, [v.ine]: b } }
            : prev));
        }
        else fallidos.push(v.ine);
      }));
      if (active && fallidos.length > 0) {
        console.warn(`[VECMUN] ${fallidos.length} contorno(s) vecino(s) sin cargar (${fallidos.join(', ')}): no se pintan.`);
      }
    };
    // Un JSON remoto inesperado no debe tumbar el paso (DOP-2): se avisa y el mapa sigue sin vecinos.
    void cargar().catch((err: unknown) => {
      if (active) console.warn(`[VECMUN] Error al cargar los términos vecinos: ${err instanceof Error ? err.message : String(err)}`);
    });
    return () => { active = false; };
  }, [ine5, ineUtil]);

  // Lo cargado vale solo para el municipio actual (y solo con INE útil).
  const actual = ineUtil && carga?.ine5 === ine5 ? carga : null;
  const propio = actual?.propio ?? null;

  // Posición e icono de cada nombre, estables mientras no cambie el municipio. Leaflet compara por
  // identidad: con arrays e iconos nuevos en cada render llamaría a `setLatLng`/`setIcon` en todos
  // los nombres en cada cambio de zoom y en cada contorno que llega.
  const vecinosActuales = actual?.vecinos;
  const etiquetas = useMemo(() => {
    const m = new Map<string, { pos: LatLngTuple; icono: L.DivIcon }>();
    for (const v of vecinosActuales ?? []) m.set(v.ine, { pos: [v.lat, v.lng], icono: crearIcono(v.nombre, false) });
    return m;
  }, [vecinosActuales]);

  // Solo los vecinos cuyo contorno llegó: sin contorno no se pinta ni el nombre.
  const cargados = useMemo(
    () => (actual ? actual.vecinos.flatMap((v) => {
      const b = actual.contornos[v.ine];
      const etiqueta = etiquetas.get(v.ine);
      return b && etiqueta ? [{ v, geometria: b.geometry, etiqueta }] : [];
    }) : []),
    [actual, etiquetas],
  );

  const estilos = useMemo(() => {
    const w = Math.max(1, grosor - 0.5);
    return {
      halo: { fill: false, color: '#000000', weight: w + 2, opacity: 0.45 },
      linea: { fill: false, color: '#ffffff', weight: w, opacity: 0.9 },
    };
  }, [grosor]);

  const iconoPropio = useMemo(() => (propio ? crearIcono(propio.nombre, true) : null), [propio]);

  // Todos los halos por debajo de todas las líneas, llegue en el orden que llegue cada vecino.
  useEffect(() => {
    lineas.current.forEach((capa) => capa.bringToFront?.());
  }, [cargados]);

  if (!ineUtil) return null;

  const conNombres = zoom >= ZOOM_NOMBRES_MIN;

  return (
    <>
      <Pane name={PANE_LINEAS} style={{ zIndex: Z_LINEAS }}>
        {cargados.map(({ v, geometria }) => (
          <GeoJSON
            key={`${v.ine}-halo`}
            data={geometria}
            style={estilos.halo}
            interactive={false}
            pane={PANE_LINEAS}
          />
        ))}
        {cargados.map(({ v, geometria }) => (
          <GeoJSON
            key={`${v.ine}-linea`}
            ref={(capa) => {
              if (capa) lineas.current.set(v.ine, capa);
              else lineas.current.delete(v.ine);
            }}
            data={geometria}
            style={estilos.linea}
            interactive={false}
            pane={PANE_LINEAS}
          />
        ))}
      </Pane>
      <Pane name={PANE_NOMBRES} style={{ zIndex: Z_NOMBRES }}>
        {conNombres && propio && iconoPropio && (
          <Marker position={propio.pos} icon={iconoPropio} interactive={false} keyboard={false} pane={PANE_NOMBRES} />
        )}
        {conNombres && cargados.map(({ v, etiqueta }) => (
          <Marker
            key={`${v.ine}-nombre`}
            position={etiqueta.pos}
            icon={etiqueta.icono}
            interactive={false}
            keyboard={false}
            pane={PANE_NOMBRES}
          />
        ))}
      </Pane>
    </>
  );
};

export default VecinosCapa;
